import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Crop, RotateCw, ZoomIn, Check, Loader2, RotateCcw,
  FlipHorizontal2, FlipVertical2, Square, RectangleHorizontal,
  RectangleVertical, Maximize2, Minus, Plus,
} from 'lucide-react';

const ASPECT_OPTIONS = [
  { value: 1, label: '1:1', icon: Square, hint: 'Τετράγωνο (logos)' },
  { value: 4 / 3, label: '4:3', icon: RectangleHorizontal, hint: 'Standard' },
  { value: 16 / 9, label: '16:9', icon: RectangleHorizontal, hint: 'Widescreen' },
  { value: 3 / 4, label: '3:4', icon: RectangleVertical, hint: 'Portrait' },
  { value: 0, label: 'Ελεύθερο', icon: Maximize2, hint: 'Free aspect' },
];

/**
 * ImageCropperModal — advanced image cropper with zoom, rotation, flip, aspect ratios.
 */
export const ImageCropperModal = ({
  isOpen, onClose, imageSrc, aspect: defaultAspect = 1, onCropComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [aspect, setAspect] = useState(defaultAspect);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropChange = useCallback((c) => setCrop(c), []);
  const onZoomChange = useCallback((z) => setZoom(z), []);
  const onCropAreaChange = useCallback((_, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
  };

  const adjustZoom = (delta) => {
    setZoom((z) => Math.max(0.3, Math.min(5, +(z + delta).toFixed(2))));
  };

  const adjustRotation = (delta) => {
    setRotation((r) => (((r + delta) % 360) + 360) % 360);
  };

  const createCroppedImage = async () => {
    if (!croppedAreaPixels || croppedAreaPixels.width <= 0 || croppedAreaPixels.height <= 0) {
      throw new Error('Invalid crop area');
    }
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = imageSrc;
    });

    // Cap output size to keep canvas within browser memory limits.
    // Most browsers fail above ~16384px or ~268M total pixels. We cap at 2048x2048.
    const MAX_OUT = 2048;
    const cropW = Math.max(1, Math.round(croppedAreaPixels.width));
    const cropH = Math.max(1, Math.round(croppedAreaPixels.height));
    const outScale = Math.min(1, MAX_OUT / Math.max(cropW, cropH));
    const outW = Math.max(1, Math.round(cropW * outScale));
    const outH = Math.max(1, Math.round(cropH * outScale));

    const out = document.createElement('canvas');
    out.width = outW;
    out.height = outH;
    const octx = out.getContext('2d');
    octx.imageSmoothingEnabled = true;
    octx.imageSmoothingQuality = 'high';

    // Translate to output center, apply rotation/flip, then draw the source image
    // positioned so that the requested crop rectangle (in source coords) lands on the canvas.
    const radians = (rotation * Math.PI) / 180;
    octx.translate(outW / 2, outH / 2);
    octx.rotate(radians);
    octx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

    // The crop rectangle is defined in the *rotated* image's coordinate system (react-easy-crop convention).
    // Compute the center of the crop area in that rotated frame, then position the un-rotated image so
    // the center of the crop lines up with the canvas center.
    const rotatedW = Math.abs(image.width * Math.cos(radians)) + Math.abs(image.height * Math.sin(radians));
    const rotatedH = Math.abs(image.width * Math.sin(radians)) + Math.abs(image.height * Math.cos(radians));
    const cropCenterX = croppedAreaPixels.x + croppedAreaPixels.width / 2 - rotatedW / 2;
    const cropCenterY = croppedAreaPixels.y + croppedAreaPixels.height / 2 - rotatedH / 2;

    octx.scale(outScale, outScale);
    octx.drawImage(
      image,
      -image.width / 2 - cropCenterX,
      -image.height / 2 - cropCenterY,
      image.width,
      image.height
    );

    return new Promise((resolve, reject) => {
      out.toBlob(
        (blob) => {
          if (blob && blob.size > 0) resolve(blob);
          else reject(new Error('Empty blob produced'));
        },
        'image/png',
        0.95
      );
    });
  };

  const handleSave = async () => {
    setProcessing(true);
    try {
      const blob = await createCroppedImage();
      if (blob && blob.size > 100) {
        await onCropComplete(blob);
        onClose();
      } else {
        throw new Error('Empty cropped image');
      }
    } catch (e) {
      console.error('Crop failed', e);
      alert('Σφάλμα κατά το crop. Δοκιμάστε με μικρότερη εικόνα ή πατήστε Reset και ξαναπροσπαθήστε.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  // CSS transform for image preview flip (react-easy-crop handles rotation prop directly)
  const transformStyle = {
    transform: `scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[300] bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
        data-testid="image-cropper-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-navy-100 border border-gold/30 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col"
        >
          <div className="px-6 py-3 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Crop className="w-5 h-5 text-gold" />
              Επεξεργασία Εικόνας
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                disabled={processing}
                className="px-3 py-1.5 text-xs bg-slate-700/50 text-slate-300 border border-slate-600 rounded hover:bg-slate-700 inline-flex items-center gap-1.5"
                data-testid="cropper-reset-btn"
                title="Επαναφορά"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset
              </button>
              <button onClick={onClose} disabled={processing} className="text-slate-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Aspect ratio selector */}
          <div className="px-4 py-2 border-b border-slate-800 flex items-center gap-1 flex-wrap bg-navy-200/30">
            <span className="text-xs text-slate-500 mr-2">Σχήμα:</span>
            {ASPECT_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = aspect === opt.value;
              return (
                <button
                  key={opt.label}
                  onClick={() => setAspect(opt.value)}
                  title={opt.hint}
                  className={`px-2.5 py-1 rounded text-xs inline-flex items-center gap-1.5 border transition-colors ${
                    active
                      ? 'bg-gold/20 text-gold border-gold/40'
                      : 'bg-navy-200/50 text-slate-300 border-slate-700 hover:bg-navy-200'
                  }`}
                  data-testid={`cropper-aspect-${opt.label.replace(':', '-').replace(' ', '-')}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Cropper area */}
          <div className="relative bg-black flex-1" style={{ minHeight: '380px', maxHeight: 'calc(95vh - 280px)' }}>
            <div className="absolute inset-0" style={transformStyle}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect || undefined}
                minZoom={0.3}
                maxZoom={5}
                zoomSpeed={0.2}
                objectFit="contain"
                restrictPosition={false}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropAreaChange}
                showGrid={true}
              />
            </div>
            {/* Quick zoom buttons overlay */}
            <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-10">
              <button
                onClick={() => adjustZoom(0.2)}
                disabled={zoom >= 5}
                className="w-9 h-9 bg-black/70 hover:bg-black/90 border border-gold/40 text-gold rounded-lg inline-flex items-center justify-center disabled:opacity-30"
                title="Zoom In"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => adjustZoom(-0.2)}
                disabled={zoom <= 0.3}
                className="w-9 h-9 bg-black/70 hover:bg-black/90 border border-gold/40 text-gold rounded-lg inline-flex items-center justify-center disabled:opacity-30"
                title="Zoom Out"
              >
                <Minus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="p-4 space-y-2.5 bg-navy-100 border-t border-slate-800 flex-shrink-0">
            {/* Zoom */}
            <div className="flex items-center gap-3">
              <ZoomIn className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="text-xs text-slate-400 w-14">Zoom</span>
              <input
                type="range"
                min={0.3}
                max={5}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 accent-gold"
                data-testid="cropper-zoom-slider"
              />
              <span className="text-xs text-slate-400 w-14 text-right font-mono">{zoom.toFixed(2)}x</span>
            </div>

            {/* Rotation */}
            <div className="flex items-center gap-3">
              <RotateCw className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="text-xs text-slate-400 w-14">Στροφή</span>
              <input
                type="range"
                min={0}
                max={360}
                step={1}
                value={rotation}
                onChange={(e) => setRotation(parseInt(e.target.value, 10))}
                className="flex-1 accent-gold"
                data-testid="cropper-rotation-slider"
              />
              <span className="text-xs text-slate-400 w-14 text-right font-mono">{rotation}°</span>
            </div>

            {/* Quick rotation + flip buttons */}
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <span className="text-xs text-slate-500 mr-1">Γρήγορα:</span>
              <button
                onClick={() => adjustRotation(-90)}
                className="px-2.5 py-1 text-xs bg-navy-200 text-slate-300 border border-slate-700 rounded hover:bg-navy-200/70 inline-flex items-center gap-1"
                title="Στροφή -90°"
                data-testid="cropper-rotate-left"
              >
                <RotateCcw className="w-3.5 h-3.5" /> -90°
              </button>
              <button
                onClick={() => adjustRotation(90)}
                className="px-2.5 py-1 text-xs bg-navy-200 text-slate-300 border border-slate-700 rounded hover:bg-navy-200/70 inline-flex items-center gap-1"
                title="Στροφή +90°"
                data-testid="cropper-rotate-right"
              >
                <RotateCw className="w-3.5 h-3.5" /> +90°
              </button>
              <button
                onClick={() => setFlipH((v) => !v)}
                className={`px-2.5 py-1 text-xs border rounded inline-flex items-center gap-1 ${
                  flipH ? 'bg-gold/20 text-gold border-gold/40' : 'bg-navy-200 text-slate-300 border-slate-700 hover:bg-navy-200/70'
                }`}
                title="Flip Horizontal"
                data-testid="cropper-flip-h"
              >
                <FlipHorizontal2 className="w-3.5 h-3.5" /> Flip H
              </button>
              <button
                onClick={() => setFlipV((v) => !v)}
                className={`px-2.5 py-1 text-xs border rounded inline-flex items-center gap-1 ${
                  flipV ? 'bg-gold/20 text-gold border-gold/40' : 'bg-navy-200 text-slate-300 border-slate-700 hover:bg-navy-200/70'
                }`}
                title="Flip Vertical"
                data-testid="cropper-flip-v"
              >
                <FlipVertical2 className="w-3.5 h-3.5" /> Flip V
              </button>
            </div>
          </div>

          <div className="px-6 py-3 border-t border-slate-800 flex gap-3 justify-end flex-shrink-0">
            <button
              onClick={onClose}
              disabled={processing}
              className="px-4 py-2 text-slate-300 hover:text-white text-sm"
              data-testid="cropper-cancel-btn"
            >
              Ακύρωση
            </button>
            <button
              onClick={handleSave}
              disabled={processing || !croppedAreaPixels}
              className="px-5 py-2 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold inline-flex items-center gap-2 disabled:opacity-50 text-sm"
              data-testid="cropper-save-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {processing ? 'Επεξεργασία...' : 'Εφαρμογή'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageCropperModal;
